import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Drawer, Popconfirm, Tooltip, message } from 'antd';
import { ThunderboltFilled } from '@ant-design/icons';
import {
  getResumePreviewHtml,
  getResumeVersion,
  editFromTailored,
  revertSearchResume,
} from '../../services/api';
import { captureEvent } from '../../services/analytics';
import { setPendingResumeBind } from '../../utils/pendingResumeBind';
import { getApiErrorMessage } from '../../utils/errorUtils';
import PreviewPane from './PreviewPane';
import css from './TailoredForThisRoleBadge.module.scss';

export interface TailoredForThisRoleBadgeProps {
  tailoredVersionId: string;
  /** Labels the badge; equals the role this resume was tailored for. */
  role: string;
  /** Search that owns this tailored resume — used by Revert/bind (Task 6). */
  searchId: string;
  /** Refetch the search list after Revert/bind lands (Task 6). */
  onChanged: () => void;
  /** Human version ordinal of the bound tailored resume (1 = first tailor, 2 =
   *  tailored/edited again). Renders a small "v{n}" pill; absent → no pill. */
  versionOrdinal?: number | null;
}

/**
 * The single persistent surface for a search's auto-tailored resume: a quiet
 * emerald chip that names the tailored-for role and opens a read-only Preview.
 * The tailored resume is the payoff, so the chip stays understated and lets
 * Preview lead. Edit + Revert arrive in Task 6 — the props are already part of
 * the API so mounting sites don't churn.
 */
const TailoredForThisRoleBadge: React.FC<TailoredForThisRoleBadgeProps> = ({
  tailoredVersionId,
  role,
  searchId,
  onChanged,
  versionOrdinal,
}) => {
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [html, setHtml] = useState('');
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reverting, setReverting] = useState(false);

  const handlePreview = async () => {
    setPreviewOpen(true);
    setLoading(true);
    // Clear any prior preview so a later edit (Task 6) never flashes a stale
    // summary/document before the fresh fetch lands.
    setHtml('');
    setSummary(null);
    try {
      // The change summary is a nicety — a missing/failed version fetch must
      // never sink the preview, so it degrades to null on its own.
      const [previewHtml, version] = await Promise.all([
        getResumePreviewHtml(tailoredVersionId),
        getResumeVersion(tailoredVersionId).catch(() => null),
      ]);
      setHtml(previewHtml);
      setSummary(version?.fixContext?.change_summary ?? null);
    } catch {
      message.error("Couldn't load the tailored resume");
      setPreviewOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Edit is the escape hatch: fork an editable draft off the base, stash the
  // bind target so ResumeTailor's approve rebinds it to this search, then hand
  // off to the editor. On failure we surface a message and stay put — never
  // navigate, or the user lands in the editor with nothing forked.
  const handleEdit = async () => {
    setEditing(true);
    try {
      const draft = await editFromTailored(tailoredVersionId);
      setPendingResumeBind({ versionId: draft.id, searchId });
      captureEvent('auto_tailor_adjusted', { searchId, autoTailorVersionId: tailoredVersionId });
      navigate(`/resume/tailor/${draft.id}`);
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setEditing(false);
    }
  };

  // Revert steps the search back one version server-side (to the previous
  // tailored version, or to the base upload from the first tailor) and asks the
  // parent to refetch — the badge re-renders for the newly-bound version, and
  // disappears only once the search lands back on its untailored base.
  const handleRevert = async () => {
    setReverting(true);
    try {
      await revertSearchResume(searchId);
      captureEvent('auto_tailor_skipped', { searchId, autoTailorVersionId: tailoredVersionId });
      message.success('Reverted to the previous version');
      onChanged();
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setReverting(false);
    }
  };

  return (
    <>
      <div className={css.badge}>
        <ThunderboltFilled className={css.icon} aria-hidden />
        <span className={css.label}>Tailored for {role}</span>
        {versionOrdinal != null && (
          <span className={css.versionPill}>v{versionOrdinal}</span>
        )}
        <span className={css.actions}>
          <Button size="small" type="text" onClick={handlePreview}>
            Preview
          </Button>
          <Button size="small" type="text" onClick={handleEdit} loading={editing}>
            Edit
          </Button>
          <Popconfirm
            title="Revert to the previous version?"
            description="This search goes back to the version before this one."
            okText="Yes, revert"
            okButtonProps={{ danger: true, loading: reverting }}
            cancelText="Keep this version"
            onConfirm={handleRevert}
          >
            <Tooltip title="Goes back one step — to the version before this one. From your first tailored version, this restores your original upload.">
              <Button size="small" type="text" danger loading={reverting}>
                Revert to Previous Version
              </Button>
            </Tooltip>
          </Popconfirm>
        </span>
      </div>
      <Drawer
        title="Tailored resume"
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        width="min(720px, 94vw)"
        destroyOnClose
      >
        {summary && <p className={css.summary}>{summary}</p>}
        {/* PreviewPane's .pane is position:absolute; inset:0 — it collapses to
            zero height unless wrapped in a positioned box with a real height. */}
        <div className={css.previewBox}>
          <PreviewPane
            html={html}
            loading={loading}
            onSectionClick={() => {}}
            onUnwiredClick={() => {}}
          />
        </div>
      </Drawer>
    </>
  );
};

export default TailoredForThisRoleBadge;
